import type {
  BillingInterval,
  BillingPlan,
  EntitlementsRead,
  KbCapacityItem,
  PlanErrorFields,
  PlanFeature,
  PlanLimitKey,
  PlanName,
  PlanSource,
} from '../../types/api';

/** Where every "Upgrade" link goes: the plans page. */
export const UPGRADE_PATH = '/upgrade';

/**
 * The plans page with `plan` picked when the caller knows which plan lifts
 * the gate (`/upgrade?plan=studio`). Only plans checkout sells are passed on.
 */
export function upgradePath(plan?: PlanName | null): string {
  return plan === 'solo' || plan === 'studio' ? `${UPGRADE_PATH}?plan=${plan}` : UPGRADE_PATH;
}

/** USD list prices, mirroring the Paddle prices (PADDLE_BILLING_PLAN.md §2). Yearly is 10 months. */
export const PLAN_PRICE: Readonly<Record<BillingPlan, Readonly<Record<BillingInterval, number>>>> =
  {
    solo: { month: 19, year: 190 },
    studio: { month: 49, year: 490 },
  };

/** Plan names as the product writes them. The wire value is the data, not the label. */
export const PLAN_LABEL: Readonly<Record<PlanName, string>> = {
  free: 'Free',
  solo: 'Solo',
  studio: 'Studio',
  agency: 'Agency',
};

/** Every plan, cheapest first: the order a picker lists them in. */
export const PLAN_NAMES: readonly PlanName[] = ['free', 'solo', 'studio', 'agency'];

/** Where a plan came from, as staff read it. */
export const PLAN_SOURCE_LABEL: Readonly<Record<PlanSource, string>> = {
  trial: 'Trial',
  paddle: 'Paddle',
  manual: 'Manual',
};

/** Every cap, in the user's words. Keyed so a new limit key fails the build. */
export const PLAN_LIMIT_LABEL: Readonly<Record<PlanLimitKey, string>> = {
  posts: 'Posts per month',
  brands: 'Brands',
  team_members: 'Team members',
  example_posts: 'Example posts',
  products: 'Products',
  testimonials: 'Testimonials & results',
  key_dates: 'Key dates',
  storage_bytes: 'Storage (bytes)',
  assistant_credits: 'AI assistant credits',
};

export const PLAN_LIMIT_KEYS = Object.keys(PLAN_LIMIT_LABEL) as readonly PlanLimitKey[];

/**
 * Every gated feature as the subject of a sentence ("Publishing is on Solo and
 * up"). Keyed so a new feature fails the build until it has a name.
 */
export const PLAN_FEATURE_LABEL: Readonly<Record<PlanFeature, string>> = {
  publish: 'Publishing',
  edit_posts: 'The post editor',
  assistant: 'The AI assistant',
  daily_news: 'The daily news post',
  autopilot: 'Autopilot',
  private_templates: 'Private templates',
};

/** Labels that read as plural, so the verb agrees ("Private templates are"). */
const PLURAL_FEATURES: ReadonlySet<PlanFeature> = new Set<PlanFeature>(['private_templates']);

/**
 * The cheapest plan with each feature, mirroring the backend's
 * `PLAN_FEATURES`. Only a display hint for a gate drawn before any 402: the
 * 402 body's `required_plan` wins whenever there is one.
 */
const FEATURE_MIN_PLAN: Readonly<Record<PlanFeature, PlanName>> = {
  publish: 'solo',
  edit_posts: 'solo',
  assistant: 'solo',
  daily_news: 'solo',
  autopilot: 'studio',
  private_templates: 'studio',
};

/** The cheapest plan with `feature`, as the client believes it. */
export function featureMinPlan(feature: PlanFeature): PlanName {
  return FEATURE_MIN_PLAN[feature];
}

/** "Publishing is on Solo and up." / "Private templates are on Studio and up." */
export function planFeatureMessage(feature: PlanFeature, requiredPlan?: PlanName | null): string {
  const plan = PLAN_LABEL[requiredPlan ?? FEATURE_MIN_PLAN[feature]];
  const verb = PLURAL_FEATURES.has(feature) ? 'are' : 'is';
  return `${PLAN_FEATURE_LABEL[feature]} ${verb} on ${plan} and up.`;
}

/** The features a plan includes, by the client's mirror of the backend table. */
export function planIncludedFeatures(plan: PlanName): PlanFeature[] {
  const rank = PLAN_NAMES.indexOf(plan);
  return (Object.keys(FEATURE_MIN_PLAN) as PlanFeature[]).filter(
    (feature) => PLAN_NAMES.indexOf(FEATURE_MIN_PLAN[feature]) <= rank,
  );
}

/**
 * Posts, brands and team seats per plan, mirroring the backend's
 * `PLAN_LIMITS` for the caps the client draws before any 402 (the add-brand
 * button, the invite form, the plans page). The server's numbers win as soon
 * as there is a 402 body. Agency posts are the deployment's setting.
 */
export const PLAN_COUNT_LIMIT: Readonly<
  Record<'posts' | 'brands' | 'team_members', Readonly<Record<PlanName, number | null>>>
> = {
  posts: { free: 15, solo: 40, studio: 150, agency: null },
  brands: { free: 1, solo: 1, studio: 3, agency: null },
  team_members: { free: 1, solo: 1, studio: 2, agency: null },
};

/**
 * The fields of the 402 an add at `limit` would get, so the pre-emptive
 * upsell reads exactly like the server's: the cap now, and the first plan
 * above this one that holds more ("1 on Free · Studio allows 3").
 */
function countLimitFields(
  key: 'brands' | 'team_members',
  entitlements: EntitlementsRead,
  limit: number,
): PlanErrorFields {
  const table = PLAN_COUNT_LIMIT[key];
  const above = PLAN_NAMES.slice(PLAN_NAMES.indexOf(entitlements.plan) + 1);
  const next = above.find((plan) => {
    const cap = table[plan];
    return cap === null || cap > limit;
  });
  return {
    limit_key: key,
    limit,
    next_plan: next ?? null,
    next_limit: next ? table[next] : null,
  };
}

/** The 402 fields an add-brand at `limit` would get. */
export function brandLimitFields(entitlements: EntitlementsRead, limit: number): PlanErrorFields {
  return countLimitFields('brands', entitlements, limit);
}

/** The 402 fields an invite at `limit` seats would get ("2 on Studio · Agency has no limit"). */
export function teamLimitFields(entitlements: EntitlementsRead, limit: number): PlanErrorFields {
  return countLimitFields('team_members', entitlements, limit);
}

/**
 * What a full cap is called in a sentence. The limit labels, except storage:
 * the user knows those bytes as the Images & logos section.
 */
function limitSubject(key: PlanLimitKey): string {
  return key === 'storage_bytes' ? 'Images & logos' : PLAN_LIMIT_LABEL[key];
}

const MEGABYTE = 1024 * 1024;
const GIGABYTE = 1024 * MEGABYTE;

/** Bytes in MB or GB: one decimal under 10 ("1.5 GB"), none above ("48 MB"). */
export function formatPlanBytes(bytes: number): string {
  const inGigabytes = bytes >= GIGABYTE;
  const value = inGigabytes ? bytes / GIGABYTE : bytes / MEGABYTE;
  const rounded = value >= 10 ? Math.round(value) : Math.round(value * 10) / 10;
  return `${rounded} ${inGigabytes ? 'GB' : 'MB'}`;
}

/** A cap or count in its unit: bytes for storage, a plain number otherwise. */
function formatAmount(key: PlanLimitKey | undefined, value: number): string {
  return key === 'storage_bytes' ? formatPlanBytes(value) : String(value);
}

/** "Solo allows 25", "Solo has no limit", or null when no plan lifts the cap. */
function nextPlanPhrase(
  key: PlanLimitKey | undefined,
  nextPlan: PlanName | null | undefined,
  nextLimit: number | null | undefined,
): string | null {
  if (!nextPlan) return null;
  const name = PLAN_LABEL[nextPlan];
  return nextLimit === null || nextLimit === undefined
    ? `${name} has no limit`
    : `${name} allows ${formatAmount(key, nextLimit)}`;
}

export interface PlanLimitMessageOptions {
  /** The account's current plan, when the caller knows it ("100 MB on Free"). */
  plan?: PlanName | null;
  /** The section's own title, so personal brands read "Offers", not "Products". */
  subject?: string;
}

/**
 * One line for a 402 `PLAN_LIMIT_REACHED`:
 * "Images & logos are full (100 MB on Free · Solo allows 2 GB)."
 *
 * Plain text: the upgrade link is L3's Upsell, not this sentence.
 */
export function planLimitMessage(
  fields: PlanErrorFields | null,
  options: PlanLimitMessageOptions = {},
): string {
  const key = fields?.limit_key;
  const subject = options.subject ?? (key ? limitSubject(key) : null);
  if (!subject) return 'This plan is full. Upgrade to add more.';

  const parts: string[] = [];
  if (fields?.limit !== undefined && fields.limit !== null) {
    const cap = formatAmount(key, fields.limit);
    parts.push(options.plan ? `${cap} on ${PLAN_LABEL[options.plan]}` : cap);
  }
  const next = nextPlanPhrase(key, fields?.next_plan, fields?.next_limit);
  if (next) parts.push(next);

  return parts.length > 0 ? `${subject} are full (${parts.join(' · ')}).` : `${subject} are full.`;
}

/** True when a section holds as much as its plan allows (or more, after a downgrade). */
export function isCapacityFull(capacity: KbCapacityItem): boolean {
  return capacity.limit !== null && capacity.count >= capacity.limit;
}

/**
 * The meta line beside a capped section's title.
 *
 * Counts: "2 of 5" below the cap, "3 of 3 · Solo allows 10" at or above it,
 * "7 added" when unlimited. Storage: "48 MB of 100 MB · Solo allows 2 GB",
 * "48 MB used" when unlimited.
 */
export function capacityLabel(capacity: KbCapacityItem, isBytes: boolean): string {
  const key: PlanLimitKey | undefined = isBytes ? 'storage_bytes' : undefined;
  const count = formatAmount(key, capacity.count);
  if (capacity.limit === null) return isBytes ? `${count} used` : `${count} added`;

  const used = `${count} of ${formatAmount(key, capacity.limit)}`;
  const showNext = isBytes || isCapacityFull(capacity);
  const next = showNext ? nextPlanPhrase(key, capacity.next_plan, capacity.next_limit) : null;
  return next ? `${used} · ${next}` : used;
}

/**
 * A plan date, day first with a short month whatever the browser locale:
 * "20 Oct 2026", or "20 Oct" without the year for a one-line banner. The raw
 * value when it does not parse.
 */
export function formatPlanDate(value: string, options: { year?: boolean } = {}): string {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: options.year === false ? undefined : 'numeric',
  });
}

/** A feature's label mid-sentence: "the post editor", "publishing". */
function featureInSentence(feature: PlanFeature): string {
  const label = PLAN_FEATURE_LABEL[feature];
  return label.charAt(0).toLowerCase() + label.slice(1);
}

/** "a", "a and b", "a, b and c". */
function joinWithAnd(items: readonly string[]): string {
  if (items.length <= 1) return items.join('');
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

/**
 * What the next plan up adds, for the Plan card's upgrade line: "Solo adds
 * publishing, the post editor, the AI assistant and the daily news post." The
 * features the account lacks that the next plan has (by the client's mirror of
 * the backend table), or "Solo raises your limits." when it adds none. Null on
 * the top plan.
 */
export function upgradeGainsMessage(entitlements: EntitlementsRead): string | null {
  const next = entitlements.next_plan;
  if (!next) return null;
  const nextRank = PLAN_NAMES.indexOf(next);
  const gained = (Object.keys(FEATURE_MIN_PLAN) as PlanFeature[]).filter(
    (feature) =>
      !entitlements.features[feature] && PLAN_NAMES.indexOf(FEATURE_MIN_PLAN[feature]) <= nextRank,
  );
  const name = PLAN_LABEL[next];
  return gained.length > 0
    ? `${name} adds ${joinWithAnd(gained.map(featureInSentence))}.`
    : `${name} raises your limits.`;
}
